import { useEffect, useRef, useState } from 'react';

import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Container } from 'reactstrap';
import { useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import { submitZentopiaDetails } from '../../apis/integrations';
import { getReferralAmount, getReferrerDetails } from '../../apis/referral';
import Collapse from '../../components/includes/collapse';
import ToastItem from '../../components/includes/toast-item';
import CtaBanner from '../../containers/referral/ctaBanner';
import ConfidentSmileSection from '../../containers/referrals/confident-smile-section';
import CustomerCases from '../../containers/referrals/customer-cases-section';
import ReferralRewardSection from '../../containers/referrals/referral-reward-section';
import ReferralSmileJourneySection from '../../containers/referrals/referral-smile-journey';
import { pageviewEvent, startNowClickEvent,
  referralIdentify, getReferrerIdEvent } from '../../helpers/referral-segment';
import useI18n from '../../hooks/use-i18n';
import { languages, contentLanguageMap, countryDialCodeDataRequired } from '../../language.config';
import { obt3EnabledMarkets, socialLinks } from '../../links.config';
import { toast } from '../../recoil/selectors/notifications';
import getTranslationKeys, { PAGEID } from '../../utils/i18nRosseta';
import { validateEmail, screenTimeEvent, isValidName } from '../../utils/utils';

import '../../styles/globals/globals.scss';
import '../../styles/referral.scss';

const countryWithoutWhatsapp = ['jp', 'tw'];
const LAMBDA_SERVICE_TYPE = 'CP_SETTINGS';

const Referrals = () => {
  const i18n = useI18n();
  const referralLandingPage = i18n.t('referral');
  const orientationMsg = i18n.t('orientation');
  const codes = i18n.activeLocale.split('-');
  const country = codes.length === 2 ? codes[0] : 'sg';
  const lang = codes[1] || 'en';
  const socialMedia = socialLinks[country] || socialLinks.sg;

  const phoneDropdown = useRef();
  const whatsappDropdown = useRef();
  const setToast = useSetRecoilState(toast);
  const router = useRouter();
  const query = router && router.query;
  const [isPhoneCodesOpen, setIsPhoneCodesOpen] = useState(false);
  const [isWhatsAppNumberDifferent, setIsWhatsAppNumberDifferent] = useState(false);
  const [isWhatsappCodesOpen, setIsWhatsappCodesOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState(['agreeToContinue']);
  const [referralAmount, setReferralAmount] = useState({
    referredAmount: '',
    referrerAmount: ''
  });
  const [personDetails, setPersonDetails] = useState({
    name: '',
    email: '',
    phone: '',
    phoneIsdCode: countryDialCodeDataRequired[country].dialCode,
    phoneCountryCode: country,
    whatsappNumber: '',
    whatsappIsdCode: countryDialCodeDataRequired[country].dialCode,
    whatsappCountryCode: country,
    agreeToContinue: true
  });
  const [defaultReqData, setDefaultReqData] = useState({
    name: '',
    email: '',
    age: '',
    phone: '',
    whatsapp_number: '',
    agree_to_continue: false,
    utm_source: '',
    utm_source_opp: '',
    utm_medium: '',
    utm_medium_opp: '',
    utm_campaign: '',
    utm_campaign_opp: '',
    utm_content: '',
    utm_term: '',
    referral_id: '',
    eid: '',
    source_url: '',
    country_code: country,
    language_code: lang,
    go_experiment_id: 'go_experiment_id',
    finger_print_id: 'fingerprintId',
    user_ip: '',
    main_form_title: `LEAD - Lead Gen Landing Pages - ${i18n.activeLocale.toUpperCase()}`,
    entry_id: '',
    gclid: '',
    lead_source: 'Onboarding Flow',
    obt_drop_off: 'Smile questions page'
  });
  const [referrerName, setReferrerName] = useState('');

  const entryTime = new Date().getTime();

  useEffect(() => {
    loadReferralAmountDetails();
    document.addEventListener('mousedown', handleClick);
    const deviceViewed = window.innerWidth < 1024 ? 'Mobile' : 'Desktop';
    pageviewEvent('Recipient LP Viewed', { device: deviceViewed });
    return () => {
      document.removeEventListener('mousedown', handleClick);
      screenTimeEvent(entryTime, 'Recipient LP', country);
    };
  }, []);

  useEffect(() => {
    if (query && (query.utm_source || query.utm_medium || query.referral_id || query.eid)) {
      setDefaultReqData({ ...defaultReqData,
        utm_source: query.utm_source || '',
        utm_source_opp: query.utm_source || '',
        utm_medium: query.utm_medium || '',
        utm_medium_opp: query.utm_medium || '',
        utm_campaign: query.utm_campaign || '',
        utm_campaign_opp: query.utm_campaign || '',
        referral_id: query.referral_id || '',
        eid: query.eid || '',
        source_url: window.location.href });
    }
  }, [query]);

  useEffect(() => {
    if (query.referral_id) {
      loadReferrerDetails();
      getReferrerIdEvent(query.referral_id);
    }
  }, [query.referral_id]);

  useEffect(() => {
    if (!isWhatsAppNumberDifferent) {
      setPersonDetails({ ...personDetails,
        whatsappNumber: '',
        whatsappIsdCode: countryDialCodeDataRequired[country].dialCode,
        whatsappCountryCode: country });
      if (errors.includes('whatsappNumber')) {
        setErrors(errors.filter((errorField) => errorField !== 'whatsappNumber'));
      }
    }
    if (isWhatsAppNumberDifferent && !personDetails.agreeToContinue) {
      validatePhoneNumber('whatsappNumber', personDetails.whatsappNumber, personDetails.whatsappCountryCode);
    }
  }, [isWhatsAppNumberDifferent]);

  useEffect(() => {
    setErrorField('agreeToContinue', personDetails.agreeToContinue);
  }, [personDetails.agreeToContinue]);

  const loadReferralAmountDetails = async () => {
    const response = await getReferralAmount(LAMBDA_SERVICE_TYPE, country.toUpperCase(), lang);
    if (response?.referred_amount || response?.referrer_amount) {
      setReferralAmount({
        referredAmount: response.referred_amount || '',
        referrerAmount: response.referrer_amount || ''
      });
    }
  };

  const loadReferrerDetails = async () => {
    const response = await getReferrerDetails(LAMBDA_SERVICE_TYPE, lang, query.referral_id);
    if (response?.full_name) {
      setReferrerName(response.full_name.replace(/\b\w/g, (match) => match.toUpperCase()));
    }
  };

  const getFAQContent = (referralFaq) => (
    `<div class="f-md">
      <span class="content">
        ${referralFaq.answer.replace('{referred_amount}',
      referralAmount.referredAmount).replace('{referrer_amount}', referralAmount.referrerAmount)}
      </span>
      <span class=${!referralFaq.link && 'd-none'}>
          <a class="link ${referralFaq.link_label === 'nill'
      ? 'd-none' : ''}" target="_blank" href="${referralFaq.link}">${referralFaq.link_label}</a>
      </span>
    </div>`
  );

  const updateForm = (data) => {
    let updatedDetails = { ...personDetails };
    Object.keys(data).forEach((key) => {
      updatedDetails = { ...updatedDetails, [key]: data[key] };
    });
    setPersonDetails(updatedDetails);
  };

  const handleClick = (e) => {
    if ((phoneDropdown.current
      && phoneDropdown.current.contains(e.target))
      || (whatsappDropdown.current && whatsappDropdown.current.contains(e.target))) {
      return;
    }
    setIsPhoneCodesOpen(false);
    setIsWhatsappCodesOpen(false);
  };

  const setErrorField = (field, isValid) => {
    if (!isValid && !errors.includes(field)) {
      setErrors([...errors, field]);
    } else if (isValid && errors.includes(field)) {
      setErrors(errors.filter((errorField) => field !== errorField));
    }
  };

  const buttoncheck = () => {
    if (!personDetails.name
      || !personDetails.email
      || !personDetails.phone
      || (isWhatsAppNumberDifferent && !personDetails.whatsappNumber)) {
      return false;
    }
    return true;
  };

  const validateField = (event, field) => {
    if (event.keyCode === 13) {
      switch (field) {
        case 'name':
          validateName();
          break;

        case 'email':
          validateEmailId();
          break;

        case 'phone':
          validatePhoneNumber('phone', personDetails.phone, personDetails.phoneCountryCode);
          break;

        case 'whatsappNumber':
          validatePhoneNumber('whatsappNumber', personDetails.whatsappNumber, personDetails.whatsappCountryCode);
          break;

        default:
          break;
      }
    }
  };

  const validateName = () => setErrorField('name', isValidName(personDetails.name));

  const validateEmailId = () => setErrorField('email', validateEmail(personDetails.email));

  const validatePhoneNumber = (field, fieldValue, countryCode) => setErrorField(field, fieldValue
    && isValidPhoneNumber(fieldValue, countryCode.toUpperCase()));

  const onChangeOfAgreeToContinue = () => {
    const currentErrors = [];

    if (!personDetails.name) {
      currentErrors.push('name');
    }
    if (!validateEmail(personDetails.email)) {
      currentErrors.push('email');
    }
    if (!(personDetails.phone
      && isValidPhoneNumber(personDetails.phone, personDetails.phoneCountryCode.toUpperCase()))) {
      currentErrors.push('phone');
    }
    if (isWhatsAppNumberDifferent
      && !(personDetails.whatsappNumber
        && isValidPhoneNumber(personDetails.whatsappNumber, personDetails.whatsappCountryCode.toUpperCase()))) {
      currentErrors.push('whatsappNumber');
    }
    setErrors(currentErrors);
  };

  const onFormSubmit = async () => {
    setIsLoading(true);
    const phoneNumber = `${personDetails.phoneIsdCode}${personDetails.phone
      ? parsePhoneNumber(personDetails.phone, personDetails.phoneCountryCode.toUpperCase()).formatNational() : ''}`;
    let whatsappNumber = '';
    if (isWhatsAppNumberDifferent) {
      whatsappNumber = `${personDetails.whatsappIsdCode}${personDetails.whatsappNumber
        ? parsePhoneNumber(personDetails.whatsappNumber,
          personDetails.whatsappCountryCode.toUpperCase()).formatNational() : ''}`;
    } else if (country !== 'tw') {
      whatsappNumber = phoneNumber;
    }
    const reqData = { ...defaultReqData,
      ...{
        name: personDetails.name,
        email: personDetails.email,
        phone: phoneNumber,
        whatsapp_number: whatsappNumber,
        agree_to_continue: `${personDetails.agreeToContinue}`,
        form_id: uuidv4()
      } };
    const res = await submitZentopiaDetails(reqData);
    if (res && res.status === 'success') {
      setIsLoading(false);
      router.push(`${obt3EnabledMarkets.includes(country) ? process.env.NEXT_PUBLIC_OBT3_LINK
        : process.env.NEXT_PUBLIC_OBT_AI_LINK}`
        + `${i18n.activeLocale}/smile-questions?email=${reqData.email}&formId=${reqData.form_id}`);
    } else {
      setIsLoading(false);
      setToast(res?.message || 'Something went wrong. Please try again.');
    }
    startNowClickEvent();
    referralIdentify(personDetails.name, personDetails.email, phoneNumber);
  };

  return (
    <div className="wrapper referral-landing-page">
      <Head>
        <title>{referralLandingPage.page_title}</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta name="description" content={referralLandingPage.page_description} />
        <meta name="keywords" content={referralLandingPage.page_keywords} />
        <meta name="author" content="Zenyum" />
        <meta httpEquiv="content-language" content={lang} />
        <meta property="og:title" content={referralLandingPage.page_title} />
        <meta property="og:description" content={referralLandingPage.page_description} />
        <meta property="og:image" content={`${process.env.NEXT_PUBLIC_BASE_URL}img/referral-landingpage-image.png`} />
        <meta httpEquiv="content-language" content={contentLanguageMap[i18n.activeLocale]} />
        <meta name="robots" content="noindex" />

        <link rel="alternate" hrefLang="en" href="https://www.zenyum.com/sg-en" />
        <link rel="alternate" hrefLang="en-SG" href="https://www.zenyum.com/sg-en" />
        <link rel="alternate" hrefLang="en-MY" href="https://www.zenyum.com/my-en" />
        <link rel="alternate" hrefLang="en-HK" href="https://www.zenyum.com/hk-en" />
        <link rel="alternate" hrefLang="zh-HK" href="https://www.zenyum.com/hk-zh" />
        <link rel="alternate" hrefLang="zh-TW" href="https://www.zenyum.com/tw-zh" />
        <link rel="alternate" hrefLang="en-MY" href="https://www.zenyum.com/my-en" />
        <link rel="alternate" hrefLang="th" href="https://www.zenyum.com/th-th" />
        <link rel="alternate" hrefLang="id" href="https://www.zenyum.com/id-id" />
        <link rel="alternate" hrefLang="vi" href="https://www.zenyum.com/vn-vi" />

        <link rel="icon" href="/favicon_32x32.png" sizes="32x32" />
        <link rel="icon" href="/favicon_192x192.png" sizes="192x192" />
        <link rel="apple-touch-icon" href="/img/favicon_32x32.png" />
        {lang !== 'vi' && lang !== 'th' && lang !== 'zh' && (
        <link rel="stylesheet" href="/fonts/fonts.css" />
        )}
        {lang === 'vi' && <link rel="stylesheet" href="/fonts/fonts-vi.css" />}
        {lang === 'th' && <link rel="stylesheet" href="/fonts/fonts-th.css" />}
        {lang === 'zh' && <link rel="stylesheet" href="/fonts/fonts-zh.css" />}
        <link rel="stylesheet" href="/vendor/bootstrap/css/bootstrap.min.css" async={true} />
        <link rel="stylesheet" href="/vendor/slick/slick.min.css" />
        <link rel="stylesheet" href="/vendor/slick/slick-theme.min.css" defer={true} />
      </Head>
      <header className="referral-header">
        <Container>
          <nav>
            <Link href="/[lng]/" as={`/${i18n.activeLocale}/`}>
              <a className="nav-logo">
                <img src="/img/logo-dark-blue.svg" alt="Zenyum Logo" data-gumlet="false" />
              </a>
            </Link>
          </nav>
        </Container>

        <div className="orientation-msg">
          <div>
            <h2>{orientationMsg.title}</h2>
            <p>{orientationMsg.description}</p>
          </div>
        </div>
        <div className="toast-msg">
          <ToastItem />
        </div>
      </header>
      <main>
        <div className="top-section-wraper">
          <div className="referral-section-wrapper">
            <div className="hero-radial-gradient" />
            <Container>
              <div className="form-section" id="formSection">
                <div className="referral-title-container">
                  {!!referralAmount && !!referralAmount.referredAmount && !!referralAmount.referrerAmount && (
                  <>
                    <h2
                      className="title referral-title"
                      id="referralTitle"
                      data-key="referral::heading"
                      data-lokalise=""
                    >
                      {i18n.t('referral.heading', { referred_amount: referralAmount.referredAmount })}
                    </h2>
                    <h2 className="title referral-mobile-title">{referralLandingPage.mobile_heading_one}</h2>
                    <h2
                      className="title referral-mobile-title"
                      id="referralMobileTitle"
                      data-key="referral::mobile_heading_two"
                      data-lokalise=""
                    >
                      {i18n.t('referral.mobile_heading_two', { referred_amount: referralAmount.referredAmount })}
                    </h2>
                  </>)}
                </div>
                <div className={`title f-sm mb-8 ${errors.includes('name') ? 'error-title' : ''}`}>
                  <span data-key="referral::name" data-lokalise="">{referralLandingPage.name}</span>
                  <span className="madatory">*</span>
                </div>
                <input
                  type="text"
                  name="name"
                  autoComplete="name"
                  className={errors.includes('name') ? 'error-box' : ''}
                  placeholder={referralLandingPage.name_placeholder}
                  value={personDetails.name}
                  onChange={(evt) => {
                    updateForm({ name: evt.target.value });
                    setErrorField('name', true);
                  }}
                  onKeyDown={(event) => validateField(event, 'name')}
                  onBlur={validateName}
                />
                {errors.includes('name') && (
                <div className="d-flex mt-8">
                  <img src="/img/error.svg" alt="error icon" width={14} height={14} />
                  <span
                    data-key="referral::invalid_name"
                    data-lokalise=""
                    className="error-title f-xs ml-8"
                  >
                    {referralLandingPage.invalid_name}

                  </span>
                </div>
                )}

                <div className={`title f-sm mb-8 mt-16 ${errors.includes('phone') ? 'error-title' : ''}`}>
                  <span
                    data-key="referral::phone_number"
                    data-lokalise=""
                  >
                    {referralLandingPage.phone_number}
                  </span>
                  <span className="madatory">*</span>
                </div>
                <div className="d-flex phone-number-section">
                  <div
                    className={`input-default btn text-left ${errors.includes('phone') ? 'error-box' : ''}`}
                    onClick={() => setIsPhoneCodesOpen(!isPhoneCodesOpen)}
                    role="presentation"
                    ref={phoneDropdown}
                  >
                    <div className="flag-box"><div className={`iti-flag ${personDetails.phoneCountryCode}`} /></div>
                    <span className="f-sm">{personDetails.phoneIsdCode}</span>
                    <img
                      className="droparrow"
                      src="/img/clinic-selection/accordion-arrow.svg"
                      data-gumlet="false"
                      alt="down arrow"
                    />
                    <ul className={`country-list ${isPhoneCodesOpen ? 'show' : 'hide'}`} id="country-listbox">
                      {countryDialCodeDataRequired && Object.keys(countryDialCodeDataRequired).map((key) => (
                        <li
                          className="country standard"
                          role="presentation"
                          onClick={() => {
                            updateForm({ phoneIsdCode: countryDialCodeDataRequired[key].dialCode,
                              phoneCountryCode: key }); validatePhoneNumber('phone', personDetails.phone, key);
                          }}
                        >
                          <div className="flag-box"><div className={`iti-flag ${key}`} /></div>
                          <span className="country-name">{countryDialCodeDataRequired[key].name}</span>
                          <span className="dial-code">{countryDialCodeDataRequired[key].dialCode}</span>
                        </li>
                      ))}

                    </ul>
                  </div>
                  <input
                    type="text"
                    name="phone"
                    autoComplete="phone"
                    className={errors.includes('phone') ? 'error-box' : ''}
                    placeholder={referralLandingPage.phone_number_placeholder}
                    value={personDetails.phone}
                    onChange={(evt) => {
                      updateForm({ phone: evt.target.value });
                      setErrorField('phone', true);
                    }}
                    onKeyDown={(event) => validateField(event, 'phone')}
                    onBlur={() => validatePhoneNumber('phone', personDetails.phone, personDetails.phoneCountryCode)}
                  />
                </div>
                {errors.includes('phone') && (
                <div className="d-flex mt-8">
                  <img src="/img/error.svg" alt="error icon" width={14} height={14} />
                  <span
                    className="error-title f-xs ml-8"
                    data-key="referral::invalid_phone"
                    data-lokalise=""
                  >
                    {referralLandingPage.invalid_phone}
                  </span>
                </div>
                )}
                {!countryWithoutWhatsapp.includes(country) && (
                <div className={`extra-input-wrapper ${country === 'tw' ? 'd-none' : ''}`}>
                  <div className="checkout-checkbox">
                    <input
                      type="checkbox"
                      id="diffWhatsAppNumber"
                      checked={!isWhatsAppNumberDifferent}
                      onChange={() => setIsWhatsAppNumberDifferent(!isWhatsAppNumberDifferent)}
                    />
                    <label htmlFor="diffWhatsAppNumber" className="f-sm">
                      <span className="title" data-key="referral::different_whatsapp_local_number" data-lokalise="">
                        {referralLandingPage.different_whatsapp_local_number}
                      </span>
                    </label>
                  </div>
                </div>
                )}
                {!countryWithoutWhatsapp.includes(country) && (
                <div className={`mt-16 ${!isWhatsAppNumberDifferent ? 'd-none' : ''}`}>
                  <div
                    className={`title f-sm mb-8 mt-16 ${errors.includes('whatsappNumber') ? 'error-title' : ''}`}
                    data-key="referral::whatsapp_number"
                    data-lokalise=""
                  >
                    {referralLandingPage.whatsapp_number}
                  </div>
                  <div className="d-flex phone-number-section">
                    <div
                      className={`input-default btn text-left ${errors.includes('whatsappNumber')
                        ? 'error-box' : ''}`}
                      onClick={() => setIsWhatsappCodesOpen(!isWhatsappCodesOpen)}
                      role="presentation"
                      ref={whatsappDropdown}
                    >
                      <div className="flag-box">
                        <div className={`iti-flag ${personDetails.whatsappCountryCode}`} />
                      </div>
                      <span className="f-sm">{personDetails.whatsappIsdCode}</span>
                      <img
                        className="droparrow"
                        src="/img/clinic-selection/accordion-arrow.svg"
                        data-gumlet="false"
                        alt="down arrow"
                      />
                      <ul className={`country-list ${isWhatsappCodesOpen ? 'show' : 'hide'}`} id="country-listbox">
                        {countryDialCodeDataRequired && Object.keys(countryDialCodeDataRequired).map((key) => (
                          <li
                            className="country standard"
                            role="presentation"
                            onClick={() => {
                              updateForm({ whatsappIsdCode: countryDialCodeDataRequired[key].dialCode,
                                whatsappCountryCode: key });
                              validatePhoneNumber('whatsappNumber', personDetails.whatsappNumber, key);
                            }}
                          >
                            <div className="flag-box"><div className={`iti-flag ${key}`} /></div>
                            <span className="country-name">{countryDialCodeDataRequired[key].name}</span>
                            <span className="dial-code">{countryDialCodeDataRequired[key].dialCode}</span>
                          </li>
                        ))}

                      </ul>
                    </div>
                    <input
                      type="text"
                      name="whatsappNumber"
                      autoComplete="off"
                      className={errors.includes('whatsappNumber') ? 'error-box' : ''}
                      placeholder={referralLandingPage.whatsapp_number_placeholder}
                      value={personDetails.whatsappNumber}
                      onChange={(evt) => {
                        updateForm({ whatsappNumber: evt.target.value });
                        setErrorField('whatsappNumber', true);
                      }}
                      onKeyDown={(event) => validateField(event, 'whatsappNumber')}
                      onBlur={() => validatePhoneNumber('whatsappNumber', personDetails.whatsappNumber,
                        personDetails.whatsappCountryCode)}
                    />
                  </div>
                  {errors.includes('whatsappNumber') && (
                  <div className="d-flex mt-8">
                    <img src="/img/error.svg" alt="error icon" width={14} height={14} />
                    <span
                      className="error-title f-xs ml-8"
                      data-key="referral::invalid_phone"
                      data-lokalise=""
                    >
                      {referralLandingPage.invalid_phone}
                    </span>
                  </div>
                  )}
                </div>
                )}

                <div className={`title f-sm mb-8 mt-16 ${errors.includes('email') ? 'error-title' : ''}`}>
                  <span data-key="referral::email" data-lokalise="">{referralLandingPage.email}</span>
                  <span className="madatory">*</span>
                </div>
                <input
                  type="text"
                  name="email"
                  autoComplete="email"
                  className={errors.includes('email') ? 'error-box' : ''}
                  placeholder={referralLandingPage.email_placeholder}
                  value={personDetails.email}
                  onChange={(evt) => {
                    updateForm({ email: evt.target.value });
                    setErrorField('email', true);
                  }}
                  onKeyDown={(event) => validateField(event, 'email')}
                  onBlur={validateEmailId}
                />
                {errors.includes('email') && (
                <div className="d-flex mt-8">
                  <img src="/img/error.svg" alt="error icon" width={14} height={14} />
                  <span
                    className="error-title f-xs ml-8"
                    data-key="referral::invalid_email"
                    data-lokalise=""
                  >
                    {referralLandingPage.invalid_email}
                  </span>
                </div>
                )}

                <div className="extra-input-wrapper">
                  <div className="checkout-checkbox">
                    <div className="label f-sm">
                      { referralLandingPage.accept_link1 !== 'null'
                      && (
                      <span
                        className="title"
                        data-key="referral::accept_link1"
                        data-lokalise=""
                      >
                        {referralLandingPage.accept_link1}

                      </span>
                      )}
                      &nbsp;
                      { referralLandingPage.start_now_line !== 'null'
                      && (
                      <span
                        className="title in-mid-start-now"
                        data-key="referral::start_now_line"
                        data-lokalise=""
                      >
                        {referralLandingPage.start_now_line}
                      </span>
                      )}
                      &nbsp;
                      <span
                        className="title"
                        data-key="referral::accept_link2"
                        data-lokalise=""
                      >
                        {referralLandingPage.accept_link2}
                      </span>
                      <span className="terms-and-condition-referral">
                          &nbsp;
                        <a
                          className="link"
                          href={`/${i18n.activeLocale}/website-terms-and-conditions`}
                          data-key="referral::terms_and_conditions"
                          data-lokalise=""
                        >
                          {referralLandingPage.terms_and_conditions}

                        </a>
                      </span>
                      <span data-key="referral::and" data-lokalise="">
                        &nbsp;
                        {referralLandingPage.and}
                      </span>
                      <span>
                          &nbsp;
                        <a
                          className="link"
                          href={`/${i18n.activeLocale}/privacy`}
                          data-key="referral::privacy_policy"
                          data-lokalise=""
                        >
                          {referralLandingPage.privacy_policy}

                        </a>
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className={`referral-submit 
                  web-btn ${errors.length ? 'disabled' : ''} ${!buttoncheck() ? 'disabled' : ''}`}
                  onClick={() => {
                    onChangeOfAgreeToContinue();
                    if (!errors.length) {
                      onFormSubmit();
                    }
                  }}
                  data-key="referral::start_now"
                  data-lokalise=""
                >
                  {isLoading ? (<div className="loader position-absolute" />) : referralLandingPage.start_now}
                </button>
              </div>
            </Container>
            <img
              src="/img/referral/hero-image-desktop.png"
              className="hero-image-desktop"
              alt="Hero"
              data-gumlet="false"
            />
            <img
              src="/img/referral/hero-image-mobile.png"
              className="hero-image-mobile"
              alt="Hero"
              data-gumlet="false"
            />
          </div>
        </div>
        <Container>
          <ReferralSmileJourneySection referralAmount={referralAmount} />
          {country === 'tw' ? (
            <>
              <ReferralRewardSection
                translations={referralLandingPage.reward_details_section}
              />
              <ConfidentSmileSection translations={referralLandingPage.confident_smile_section} />
            </>
          ) : (
            <CustomerCases translations={referralLandingPage.customer_cases_section} />
          )}
          <div className="faq-section-wrapper">
            <div className="faq-section">
              <div className="title f-xl mb-8">{referralLandingPage.faq}</div>
              {referralLandingPage.referral_faq_questions.map((referralFaq, index) => (
                <div className="faq-collapse-wrapper" id={`${country}-faq${index + 1}`}>
                  { referralFaq.question !== 'null' && <Collapse
                    title={referralFaq.question.replace('{referred_amount}', referralAmount.referredAmount)}
                    content={getFAQContent(referralFaq)}
                    type="Referrals"
                    itemNumber={index + 1}
                    Lkey={`referral::referral_faq_questions::${index}`}
                  /> }
                </div>
              ))}
            </div>
          </div>
        </Container>
        <CtaBanner referrerName={referrerName} referralAmount={referralAmount} country={country} />
        <footer className="referral-footer">
          <div className="d-flex justify-content-center mb-24">
            <a href={socialMedia.facebook} target="_blank" rel="noreferrer" className="social-media">
              <img src="/img/fb_black_bg_white.svg" className="social-media-icon" alt="social media icon" />
            </a>
            <a href={socialMedia.instagram} target="_blank" rel="noreferrer" className="social-media ml-16">
              <img src="/img/ig_black.svg" className="social-media-icon" alt="social media icon" />
            </a>
            <a
              href={socialMedia.youtube}
              target="_blank"
              rel="noreferrer"
              className={`social-media ml-16 ${country === 'jp' ? 'd-none' : ''}`}
            >
              <img src="/img/icon_youtube_black.svg" className="social-media-icon" alt="youtube" />
            </a>
          </div>
          <div className="content f-md">{referralLandingPage.zenyum_pte_ltd}</div>
          <div className="content f-md mb-16">{referralLandingPage.zenyum_address}</div>
          {country !== 'jp'
          && (
          <div
            className="content f-xs mb-8"
            data-key="referral::partner_clinics_islandwide"
            data-lokalise=""
          >
            {referralLandingPage.partner_clinics_islandwide}
          </div>
          )}
          <div
            className="content f-xs mb-8"
            data-key="referral::footer_text"
            data-lokalise=""
          >
            {referralLandingPage.footer_text}
          </div>
          <div
            className="content f-xs"
            data-key="referral::copy_right"
            data-lokalise=""
          >
            {referralLandingPage.copy_right}
          </div>
        </footer>
      </main>
    </div>
  );
};

export async function getStaticProps({ params }) {
  const lngDict = {};
  const pageId = PAGEID.REFERRALS;
  const keys = getTranslationKeys(pageId);
  const { default: lngDictAll = {} } = await import(
    `../../locales/${contentLanguageMap[params.lng]}.json`
  );
  keys.forEach((data) => {
    lngDict[data] = lngDictAll[data];
  });
  return {
    props: { lng: params.lng, lngDict, pageId }
  };
}

export async function getStaticPaths() {
  return {
    paths: languages.map((l) => ({ params: { lng: l } })),
    fallback: false
  };
}

export default Referrals;
